import type { ReactNode } from 'react';
import type {
  AssignReason,
  Order,
  OrderChannel,
  OrderSource,
  OrderStatus,
  PaymentType,
} from '../api/types';
import { sourceLetter, t, type MessageKey, type ScraperLetter } from '@aquabox/shared';
import {
  CHANNEL_COLOR,
  channelLabel,
  paymentLabel,
  panelStage,
  statusColor,
  statusText,
} from '../lib/labels';
import {
  channelStatusNote,
  externalStage,
  unifiedStatus,
  unifiedStatusColor,
  type ExternalStage,
} from '../lib/external-stage';

/**
 * Kanal pill'i (kullanıcı kararı 30.09.2026):
 *
 *   Marka uygulaması (BRAND_APP) + panel kaynağı (HAYAT_PANEL/ABANT_PANEL) ise kanal metni
 *   "Hayat uygulaması"/"Abant uygulaması" olur; pill başında marka ADI küçük yazıyla + ince
 *   ayraç + kanal görünür. Örnek: `ABANT │ ● Abant uygulaması`. Rozet gösterilmez çünkü
 *   marka adı aynı bilgiyi zaten verir.
 *
 *   Diğer kaynaklar için (HAYAT_PANEL/ABANT_PANEL + BRAND_APP değil) tek harf rozeti
 *   (`source-letter-*`) + kanal adı görünür. Bu test-id'ler eski bileşen testlerinde
 *   kullanıldığı için korunur.
 */
const BRAND_TEXT: Partial<Record<ScraperLetter, string>> = {
  H: 'HAYAT',
  A: 'ABANT',
};

function brandInkColor(letter: ScraperLetter | null): string {
  if (letter === 'A') return 'var(--ch-abant-ink)';
  if (letter === 'H') return 'var(--ch-hayat)';
  return 'var(--text-secondary)';
}

function brandText(letter: ScraperLetter): string {
  return BRAND_TEXT[letter] ?? letter;
}

export function SourceLetter({ letter }: { letter: ScraperLetter }) {
  return (
    <span
      aria-hidden
      data-testid={`source-letter-${letter}`}
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: brandInkColor(letter),
        marginRight: 6,
        paddingRight: 6,
        borderRight: '1px solid var(--border)',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      {brandText(letter)}
    </span>
  );
}

export function ChannelPill({
  channel,
  source,
  compact = false,
}: {
  channel: OrderChannel;
  source?: OrderSource;
  /** Liste modu: tek kanal rozeti; kaynak paneli rozet yerine altında ikincil gri metin. */
  compact?: boolean;
}) {
  const color = CHANNEL_COLOR[channel];
  // Marka uygulaması rengi açık olduğu için metni koyu yazılır (§1.1).
  const text = channel === 'BRAND_APP' ? 'var(--text-primary)' : color;
  const letter = source !== undefined ? sourceLetter(source) : null;
  const panelBrand = letter === 'H' || letter === 'A' ? letter : null;
  // Marka uygulamasından gelen siparişte kanal metni markayı zaten söyler ("Abant uygulaması").
  const isBrandAppOnPanel = channel === 'BRAND_APP' && panelBrand !== null;
  const channelText = isBrandAppOnPanel
    ? t(panelBrand === 'H' ? 'channelBrand.hayatApp' : 'channelBrand.abantApp')
    : channelLabel(channel);
  // Yalnız marka paneli öne yazılır; Getir/Trendyol/Yemeksepeti harfi kanal adını tekrarlıyordu.
  const prefix = panelBrand !== null && !isBrandAppOnPanel && !compact ? panelBrand : null;
  const viaPanel =
    compact && panelBrand !== null && !isBrandAppOnPanel
      ? t('orders.channel.viaPanel', {
          brand: t(
            panelBrand === 'H' ? 'panelStage.brand.HAYAT_PANEL' : 'panelStage.brand.ABANT_PANEL',
          ),
        })
      : null;
  const pill = (
    <span
      className="pill"
      data-testid={isBrandAppOnPanel ? `channel-pill-brand-${letter}` : undefined}
      style={{
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        color: text,
        fontWeight: 500,
        maxWidth: '100%',
      }}
    >
      {prefix !== null ? <SourceLetter letter={prefix} /> : null}
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={channelText}
      >
        {channelText}
      </span>
    </span>
  );
  if (!viaPanel) return pill;
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-start',
        minWidth: 0,
      }}
    >
      {pill}
      <span
        data-testid="channel-via-panel"
        style={{ fontSize: 11, color: 'var(--text-secondary)' }}
      >
        {viaPanel}
      </span>
    </span>
  );
}

/**
 * Durum göstergesi: 8 px nokta + metin, durum rengi (§1.6).
 *
 * `HANDLED_EXTERNALLY` + ham durum verilirse etiket ve renk paneldeki GERÇEK aşamaya göre
 * ayarlanır (teslim edildi → yeşil "Panelde teslim edildi", iptal → kırmızı "Panelde iptal",
 * panelde işlemde → kehribar "Panelde işlemde"). Bu prop verilmezse eski tek "Panelde" etiketi
 * kullanılır; renkler `statusColor` üzerinden gelir, marka ayrımı yapılmaz.
 */
export function StatusDot({
  status,
  externalStatusRaw,
}: {
  status: OrderStatus;
  externalStatusRaw?: string | null;
}) {
  const color = statusColor(status, externalStatusRaw ?? null);
  // Ham durum yoksa `statusText` genel etiketi döner; tek yol yeterli.
  const text = statusText(status, externalStatusRaw ?? null);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        color,
        fontWeight: 600,
        fontSize: 12.5,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {text}
    </span>
  );
}

/**
 * Panel aşaması rozeti (kullanıcı kararı 30.09.2026): HAYAT_PANEL/ABANT_PANEL siparişlerinde
 * panelin GERÇEK aşamasını gösterir — `brand` + ham durum metni ("Hayat · Yolda", "Abant ·
 * Araç Listesinde"). Marka adı ayrı i18n anahtarından gelir; ham metin olduğu gibi basılır,
 * panelin kelimesi değiştirilmez.
 *
 * `panelStage` null dönerse hiçbir şey çizilmez — kaynak panel değilse ya da ham durum boşsa
 * çağıran yerde zaten '—' ya da boş gösterilir.
 */
export function PanelStagePill({
  source,
  raw,
}: {
  source: string;
  raw: string | null | undefined;
}) {
  const stage = panelStage(source, raw);
  if (!stage) return null;
  // Yumuşak zemin + ton rengi yazı + ince çerçeve: tablo satırında okunur ama bağırmaz
  // (dolu kehribar zemin üstünde beyaz yazı hem düşük kontrastlı hem durum noktasını bastırıyordu).
  const toneBg: Record<typeof stage.tone, string> = {
    new: 'var(--primary-bg)',
    progress: 'var(--bg-warning-soft)',
    done: 'var(--bg-success-soft)',
    cancelled: 'var(--bg-danger-soft)',
  };
  const toneText: Record<typeof stage.tone, string> = {
    new: 'var(--primary)',
    progress: 'var(--warning)',
    done: 'var(--success)',
    cancelled: 'var(--danger)',
  };
  return (
    <span
      data-testid={`panel-stage-pill`}
      title={stage.text}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        background: toneBg[stage.tone],
        color: toneText[stage.tone],
        boxShadow: 'inset 0 0 0 1px currentColor',
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span>{stage.brand}</span>
      <span aria-hidden style={{ opacity: 0.7 }}>
        ·
      </span>
      <span>{stage.text}</span>
    </span>
  );
}

export function PaymentPill({ payment, locked }: { payment: PaymentType; locked?: boolean }) {
  const isCredit = payment === 'CREDIT';
  return (
    <span
      className="pill"
      style={{
        background: isCredit ? 'rgba(232,163,23,.15)' : 'var(--primary-bg)',
        color: isCredit ? 'var(--warning)' : 'var(--primary)',
      }}
      title={locked ? paymentLabel(payment) : undefined}
    >
      {paymentLabel(payment)}
      {locked ? ' · kilitli' : ''}
    </span>
  );
}

/**
 * Dış panel siparişinin genişletilmiş rozeti: `externalStage()` kategorisini gösterir.
 *
 * PanelStagePill'den farkı:
 *   - "Yolda" + ≥ 2 saat → kehribar "Uzun süredir yolda" + "5 sa" uyarısı
 *   - "Ödeme Başarısız" → kırmızı ama iptalden AYRI metin
 *   - "Yolda" (Abant) kehribar kehribar tonuyla gösterilir
 *
 * `PanelStagePill` değiştirilmez — mevcut arayanlar aynı davranır.
 */
export function ExternalStagePill({
  source,
  status,
  externalStatusRaw,
  receivedAt,
}: {
  source: string;
  status: string;
  externalStatusRaw: string | null | undefined;
  receivedAt: string;
}) {
  const stage = externalStage({ source, status, externalStatusRaw, receivedAt });
  if (!stage) return null;

  // Renkler: PanelStagePill desenine sadık kalıyoruz, sadece yeni tonlar ekleniyor.
  const bg: Record<ExternalStage, string> = {
    NEW: 'var(--primary-bg)',
    ON_ROUTE: 'color-mix(in srgb, var(--warning) 18%, transparent)',
    ON_ROUTE_LONG: 'color-mix(in srgb, var(--warning) 28%, transparent)',
    DELIVERED: 'var(--bg-success-soft)',
    CANCELLED: 'var(--bg-danger-soft)',
    PAYMENT_FAILED: 'color-mix(in srgb, var(--danger) 15%, transparent)',
  };
  const text: Record<ExternalStage, string> = {
    NEW: 'var(--primary)',
    ON_ROUTE: 'var(--warning)',
    ON_ROUTE_LONG: 'var(--warning)',
    DELIVERED: 'var(--success)',
    CANCELLED: 'var(--danger)',
    PAYMENT_FAILED: 'var(--danger)',
  };

  const messages: Record<ExternalStage, string> = {
    NEW: t('externalStage.NEW'),
    ON_ROUTE: t('externalStage.ON_ROUTE'),
    ON_ROUTE_LONG: t('externalStage.ON_ROUTE_LONG'),
    DELIVERED: t('externalStage.DELIVERED'),
    CANCELLED: t('externalStage.CANCELLED'),
    PAYMENT_FAILED: t('externalStage.PAYMENT_FAILED'),
  };

  const label = messages[stage];

  // ON_ROUTE_LONG: "5 sa" gibi yaş göster
  let extra = '';
  if (stage === 'ON_ROUTE_LONG') {
    const ageMs = Date.now() - new Date(receivedAt).getTime();
    const ageHours = Math.floor(ageMs / (60 * 60 * 1000));
    extra = ` · ${t('externalStage.ageHours', { n: ageHours })}`;
  }

  return (
    <span
      data-testid={`external-stage-pill`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        background: bg[stage],
        color: text[stage],
        boxShadow: 'inset 0 0 0 1px currentColor',
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {extra}
    </span>
  );
}

/** Marka harf kutusu: H = Hayat, A = Abant (§1.6). */
export function BrandLetter({ letter }: { letter: 'H' | 'A' }) {
  const isHayat = letter === 'H';
  return (
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: 'var(--r-letter)',
        background: isHayat ? 'var(--ch-hayat)' : 'var(--ch-abant)',
        color: isHayat ? '#fff' : 'var(--navy-900)',
        fontWeight: 700,
        fontSize: 11,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {letter}
    </span>
  );
}

export type BadgeTone = 'amber' | 'blue' | 'red' | 'green' | 'neutral';

const BADGE_CLASS: Record<BadgeTone, string> = {
  amber: 'badge-amber',
  blue: 'badge-blue',
  red: 'badge-red',
  green: 'badge-green',
  neutral: 'badge-neutral',
};

/**
 * Düz rozet (sayı/etiket). `onClick` verilirse tıklanabilir `<button>` olur; verilmezse `<span>`.
 * Renkler `styles/base.css` içindeki `badge-*` sınıflarından gelir.
 */
export function Badge({
  tone,
  children,
  onClick,
}: {
  tone: BadgeTone;
  children: ReactNode;
  onClick?: () => void;
}) {
  const className = BADGE_CLASS[tone];
  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        style={{ border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
      >
        {children}
      </button>
    );
  }
  return <span className={className}>{children}</span>;
}

const ASSIGN_REASON_TONE: Record<AssignReason, BadgeTone> = {
  PANEL_LABEL: 'blue',
  STREET: 'blue',
  REGION: 'blue',
  HISTORY: 'amber',
  MANUAL: 'neutral',
  UNMATCHED: 'red',
};

/**
 * Atama gerekçesi rozeti (A1): siparişi neyin araca bağladığı. `assignReason` null ise (henüz karar
 * yok ya da araç boşaltıldı) hiçbir şey çizilmez.
 */
export function AssignReasonBadge({ order }: { order: Pick<Order, 'id' | 'assignReason'> }) {
  const reason = order.assignReason;
  if (!reason) return null;
  return (
    <span data-testid={'assign-reason-' + order.id} data-reason={reason}>
      <Badge tone={ASSIGN_REASON_TONE[reason]}>
        {t(('assign.reason.' + reason) as MessageKey)}
      </Badge>
    </span>
  );
}

const REGION_MATCH_TONE: Record<'EXACT' | 'ESTIMATED' | 'NONE', BadgeTone> = {
  EXACT: 'green',
  ESTIMATED: 'amber',
  NONE: 'red',
};

/**
 * Bölge eşleşme güveni rozeti (BA2): kesin / tahmini / bulunamadı. Alan yoksa hiçbir şey çizmez.
 * Kırmızı "bulunamadı" YALNIZ elle atama bekleyen siparişte çizilir (NEW/CONFIRMED, araç yok, panelde
 * araç etiketi yok): teslim/iptal/başarısız/panelde kapanmış ya da araç verilmiş siparişte iş yoktur.
 */
export function RegionMatchBadge({
  order,
  hideExact = false,
}: {
  order: Pick<Order, 'id' | 'regionMatch' | 'status' | 'vehicle' | 'externalVehicleRaw'>;
  /** Kesin eşleşmede rozet çizilmez (listede yalnız tahmini / bulunamadı dikkat ister). */
  hideExact?: boolean;
}) {
  const match = order.regionMatch;
  if (!match) return null;
  if (match === 'EXACT' && hideExact) return null;
  if (match === 'NONE') {
    const awaitingManual =
      (order.status === 'NEW' || order.status === 'CONFIRMED') &&
      !order.vehicle &&
      !(order.externalVehicleRaw ?? '').trim();
    if (!awaitingManual) return null;
  }
  return (
    <span
      data-testid={'region-match-' + order.id}
      data-match={match}
      title={t(('regionMatch.hint.' + match) as MessageKey)}
    >
      <Badge tone={REGION_MATCH_TONE[match]}>{t(('regionMatch.' + match) as MessageKey)}</Badge>
    </span>
  );
}

/**
 * Siparişler listesi durum hücresi: TEK rozet (Yeni / Hazırlanıyor / Yolda / Teslim Edildi / İptal /
 * Beklemede). Kanal durumu bizimkinden farklıysa altında küçük gri tek satır ("Abant: Yolda").
 */
export function OrderStatusCell({
  order,
}: {
  order: Pick<Order, 'id' | 'source' | 'status' | 'externalStatusRaw'>;
}) {
  const u = unifiedStatus(order.status, order.externalStatusRaw);
  const color = unifiedStatusColor(u);
  const note = channelStatusNote(order.source, order.status, order.externalStatusRaw);
  const brand = panelStage(order.source, note)?.brand ?? '';
  return (
    <span data-testid={'order-status-' + order.id} data-status={u}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          color,
          fontWeight: 600,
          fontSize: 12.5,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
        {t(('orders.status.' + u) as MessageKey)}
      </span>
      {note ? (
        <span
          data-testid={'order-status-note-' + order.id}
          style={{
            display: 'block',
            fontSize: 11,
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('orders.status.channelNote', { brand, text: note })}
        </span>
      ) : null}
    </span>
  );
}
